<template>
  <div class="reward-catalog">
    <!-- Header -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-4">
        <h3 class="text-lg font-semibold text-gray-900">{{ $t('loyalty.title.rewards') }}</h3>
        <div class="flex items-center space-x-3">
          <el-select
            v-model="filters.type"
            :placeholder="$t('loyalty.label.reward_type')"
            clearable
            size="small"
            style="width: 180px;">
            <el-option :label="$t('label.default')" value=""></el-option>
            <el-option :label="$t('loyalty.reward_type.discount')" value="discount"></el-option>
            <el-option :label="$t('loyalty.reward_type.free_item')" value="item"></el-option>
            <el-option :label="$t('loyalty.reward_type.cashback')" value="cash"></el-option>
            <el-option :label="$t('loyalty.reward_type.voucher')" value="voucher"></el-option>
          </el-select>
          <button
            @click="showCreateDialog = true"
            class="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-sm hover:shadow-md">
            <i class="mdi mdi-plus mr-2"></i>
            {{ $t('loyalty.button.add_reward') }}
          </button>
        </div>
      </div>

      <!-- Rewards Grid -->
      <div class="p-6" v-loading="loading">
        <div v-if="filteredRewards.length === 0" class="text-center py-12">
          <i class="mdi mdi-information-outline text-4xl text-gray-400 mb-2"></i>
          <p class="text-gray-500">{{ $t('message.no_data') }}</p>
        </div>
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <div
            v-for="reward in filteredRewards"
            :key="reward.id"
            @click="selectReward(reward)"
            class="bg-white border-2 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-200 cursor-pointer transform hover:-translate-y-1"
            :class="reward.is_active ? 'border-blue-300' : 'border-gray-200 opacity-75'">
            <div class="h-40 bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center" v-if="!reward.image">
              <i class="mdi mdi-gift text-6xl text-white opacity-50"></i>
            </div>
            <div v-else class="h-40 overflow-hidden">
              <img :src="reward.image" :alt="reward.name" class="w-full h-full object-cover">
            </div>
            <div class="p-4">
              <h4 class="text-lg font-bold text-gray-900 mb-2">{{ reward.name }}</h4>
              <p class="text-sm text-gray-600 mb-3 line-clamp-2">{{ reward.description }}</p>
              <div class="flex items-center justify-between mb-3">
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {{ reward.reward_type }}
                </span>
                <span class="text-lg font-bold text-orange-600">
                  <i class="mdi mdi-star mr-1"></i>
                  {{ reward.points_required.toLocaleString() }}
                </span>
              </div>
              <div class="flex items-center justify-between">
                <span
                  :class="reward.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium">
                  {{ reward.is_active ? $t('loyalty.status.active') : $t('loyalty.status.inactive') }}
                </span>
                <div class="flex space-x-2">
                  <button
                    @click.stop="editReward(reward)"
                    class="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                    <i class="mdi mdi-pencil text-sm"></i>
                  </button>
                  <button
                    @click.stop="deleteReward(reward.id)"
                    class="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <i class="mdi mdi-delete text-sm"></i>
                  </button>
                </div>
              </div>
              <div v-if="reward.stock_quantity !== null" class="mt-2 text-xs text-gray-500">
                {{ reward.stock_quantity - reward.redeemed_count }} {{ $t('loyalty.label.reward_remaining') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Reward Dialog -->
    <reward-form-dialog
      :visible="showCreateDialog || showEditDialog"
      :reward="selectedReward"
      :program-id="programId"
      @saved="handleRewardSaved"
      @cancelled="closeDialog">
    </reward-form-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'
import RewardFormDialog from './RewardFormDialog.vue'

export default {
  name: 'RewardCatalog',
  components: {
    RewardFormDialog
  },
  mixins: [ErrorHandlingMixin],
  props: {
    programId: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      rewards: [],
      loading: false,
      filters: {
        type: ''
      },
      showCreateDialog: false,
      showEditDialog: false,
      selectedReward: null
    }
  },
  computed: {
    filteredRewards () {
      if (!this.filters.type) {
        return this.rewards
      }
      return this.rewards.filter(r => r.reward_type === this.filters.type)
    }
  },
  async mounted () {
    await this.fetchRewards()
  },
  methods: {
    async fetchRewards () {
      try {
        this.loading = true
        this.rewards = await this.$store.dispatch('loyalty/rewards/fetchRewards', this.programId)
      } catch (error) {
        console.error('Error fetching rewards:', error)
      } finally {
        this.loading = false
      }
    },
    selectReward (reward) {
      this.$emit('reward-selected', reward.id)
    },
    editReward (reward) {
      this.selectedReward = reward
      this.showEditDialog = true
    },
    async deleteReward (rewardId) {
      this.askQuestion(this.$t('loyalty.confirm.delete_reward'), async () => {
        try {
          await this.withLoading(async () => {
            await this.$store.dispatch('loyalty/rewards/deleteReward', {
              id: rewardId,
              programId: this.programId
            })
            await this.fetchRewards()
            this.showSuccess(this.$t('loyalty.message.reward_deleted'))
            this.$emit('reward-deleted', rewardId)
          }, 'loading')
        } catch (error) {
          console.error('Error deleting reward:', error)
        }
      })
    },
    handleRewardSaved () {
      this.closeDialog()
      this.fetchRewards()
      this.$emit('reward-created')
    },
    closeDialog () {
      this.showCreateDialog = false
      this.showEditDialog = false
      this.selectedReward = null
    }
  }
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
